import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AppFeedbackPage } from '@shared-ui/app-feedback';
import { createThreadApi, ADMIN_FEEDBACK_OWNER_ID } from '@shared-ui/threads';
import type { ThreadObjectType } from '@shared-ui/threads';
import { LatexText } from '@shared-ui/latex';
import { fetchWithAdmin } from '../../api/fetchHelper';
import { adminApi } from '../../api/adminApi';
import { questionApi } from '../../api/questionApi';
import { authApi } from '../../api/auth';
import { adminThreadApi } from '../../api/adminThreadApi';
import { tenantsApi } from '../../api/tenantsApi';
import { operatesAcrossTenants } from '../../config/adminRoles';
import { useAdminUser } from '../../hooks/useAdminUser';
import { ScopedUserSearchFilter } from '../../components/ScopedUserSearchFilter';

// Admin mode → list/unread queries target the whole sentinel partition.
const adminApiClient = { fetchApi: fetchWithAdmin };
const threadApi = createThreadApi(adminApiClient);

// Still partial — a type with no entry renders no link at all (see
// `canNavigateObject` below), rather than a link whose click does nothing.
// `session_report` threads carry the SESSION id while the report page is keyed
// by run id, so it routes via the by-session resolver.
const OBJECT_ROUTES: Partial<Record<ThreadObjectType, (id: string) => string>> = {
    submission: (id) => `/submissions/${id}`,
    question: (id) => `/questions?id=${id}`,
    hint: (id) => `/hints/${id}`,
    session_report: (id) => `/questionsessionreport/by-session/${id}`,
};

function formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function AdminFeedbackPage() {
    const navigate = useNavigate();
    const currentUserId = authApi.getUser()?.id;
    const queryClient = useQueryClient();
    // Tenant filter: only for admins whose view spans tenants — same rule as the
    // sidebar's TenantFilter, and the same names endpoint it reads.
    const crossTenant = operatesAcrossTenants(useAdminUser());
    const { data: tenantNames } = useQuery({
        queryKey: ['platform-tenant-names-for-filter'],
        queryFn: () => tenantsApi.names(),
        staleTime: 60_000,
        enabled: crossTenant,
    });
    const tenantOptions = crossTenant
        ? (tenantNames?.tenants ?? []).map((t) => ({ value: t.id, label: t.name || t.id }))
        : undefined;
    // Pass-through so the update's result still reaches the page.
    const refreshBadge = <T,>(updated: T): T => {
        queryClient.invalidateQueries({ queryKey: ['admin-feedback-open'] });
        return updated;
    };

    return (
        <AppFeedbackPage
            api={threadApi}
            currentUserId={currentUserId}
            onNavigateObject={(objectType, objectId) => {
                const route = OBJECT_ROUTES[objectType as ThreadObjectType];
                if (route) navigate(route(objectId));
            }}
            canNavigateObject={(objectType) => !!OBJECT_ROUTES[objectType as ThreadObjectType]}
            adminMode={{
                // /console/threads: tenant-scoped the admin way, and it takes `tenantId`.
                listApi: adminThreadApi,
                tenantOptions,
                renderUserFilter: (value, onChange) => (
                    <ScopedUserSearchFilter
                        value={value}
                        onChange={onChange}
                        onPick={(o) => onChange(o?.email ?? '')}
                        placeholder="Filter by user…"
                    />
                ),
                // A status change moves the open/in-progress nav badge — refresh it now.
                updateStatus: (thread, status) =>
                    threadApi.update(thread.id, ADMIN_FEEDBACK_OWNER_ID, { status }).then(refreshBadge),
                saveFeedback: (thread, changes) =>
                    threadApi.update(thread.id, ADMIN_FEEDBACK_OWNER_ID, changes).then(refreshBadge),
                addMessage: (thread, message, opts) =>
                    threadApi.addMessage(thread.id, ADMIN_FEEDBACK_OWNER_ID, message, undefined, opts),
                editMessage: (thread, messageId, message) =>
                    threadApi.editMessage(thread.id, messageId, ADMIN_FEEDBACK_OWNER_ID, message),
                reassign: (thread, email) =>
                    threadApi.update(thread.id, ADMIN_FEEDBACK_OWNER_ID, { reassignToCreatorEmail: email }),
                renderDetailExtra: (thread) => {
                    if (!thread.objectType || !thread.objectId) return null;
                    if (thread.objectType === 'submission') {
                        return <SubmissionInfo objectId={thread.objectId} onNavigate={() => navigate(OBJECT_ROUTES.submission!(thread.objectId!))} />;
                    }
                    if (thread.objectType === 'question') {
                        return <QuestionInfo objectId={thread.objectId} onNavigate={() => navigate(OBJECT_ROUTES.question!(thread.objectId!))} />;
                    }
                    return null;
                },
            }}
        />
    );
}

function SubmissionInfo({ objectId, onNavigate }: { objectId: string; onNavigate: () => void }) {
    const { data: submission, isLoading, error } = useQuery({
        queryKey: ['submission-info', objectId],
        queryFn: () => adminApi.getSubmission(objectId),
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <div className="feedback-linked-detail">
                <div className="feedback-linked-detail-header">
                    <span className="feedback-linked-detail-type">Submission</span>
                </div>
                <p className="feedback-linked-detail-loading">Loading submission details...</p>
            </div>
        );
    }

    if (error || !submission) {
        return (
            <div className="feedback-linked-detail">
                <div className="feedback-linked-detail-header">
                    <span className="feedback-linked-detail-type">Submission</span>
                    <button className="feedback-linked-object-link" onClick={onNavigate}>View submission &rarr;</button>
                </div>
                <p className="feedback-linked-detail-loading">Could not load submission details.</p>
            </div>
        );
    }

    const question = submission.question;
    const user = submission.user;
    const fb = submission.feedback;

    return (
        <div className="feedback-linked-detail">
            <div className="feedback-linked-detail-header">
                <span className="feedback-linked-detail-type">Submission</span>
                <button className="feedback-linked-object-link" onClick={onNavigate}>View submission &rarr;</button>
            </div>
            {user && <InfoRow label="Student" value={`${user.name} (${user.email})`} />}
            {question && <InfoRow label="Question" value={<LatexText text={question.questionText} />} truncate />}
            {question && <InfoRow label="Subject" value={`${question.subject}${question.difficulty ? ` / ${question.difficulty}` : ''}`} />}
            {fb?.computedScores && (
                <InfoRow label="Score" value={
                    fb.computedScores.finalScore !== undefined ? `${fb.computedScores.finalScore}/10` : '-'
                } />
            )}
            <InfoRow label="Submitted" value={formatDate(submission.submittedAt)} />
        </div>
    );
}

function QuestionInfo({ objectId, onNavigate }: { objectId: string; onNavigate: () => void }) {
    const { data: question, isLoading, error } = useQuery({
        queryKey: ['feedback-question-info', objectId],
        queryFn: () => questionApi.getQuestionById(objectId),
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading) {
        return (
            <div className="feedback-linked-detail">
                <div className="feedback-linked-detail-header">
                    <span className="feedback-linked-detail-type">Question</span>
                </div>
                <p className="feedback-linked-detail-loading">Loading question…</p>
            </div>
        );
    }

    if (error || !question) {
        return (
            <div className="feedback-linked-detail">
                <div className="feedback-linked-detail-header">
                    <span className="feedback-linked-detail-type">Question</span>
                    <button className="feedback-linked-object-link" onClick={onNavigate}>View question &rarr;</button>
                </div>
                <p className="feedback-linked-detail-loading">Could not load question.</p>
            </div>
        );
    }

    return (
        <div className="feedback-linked-detail">
            <div className="feedback-linked-detail-header">
                <span className="feedback-linked-detail-type">Question</span>
                <button className="feedback-linked-object-link" onClick={onNavigate}>View question &rarr;</button>
            </div>
            <InfoRow label="Question" value={<LatexText text={question.questionText} />} truncate />
            <InfoRow label="Subject" value={`${question.subject}${question.difficulty ? ` / ${question.difficulty}` : ''}`} />
            {question.module?.id && <InfoRow label="Module" value={question.module.name || question.module.id} />}
            {question.correctAnswer && <InfoRow label="Correct answer" value={<LatexText text={question.correctAnswer} />} truncate />}
        </div>
    );
}

function InfoRow({ label, value, truncate }: { label: string; value: React.ReactNode; truncate?: boolean }) {
    return (
        <div className="feedback-linked-detail-row">
            <span className="feedback-linked-detail-label">{label}</span>
            <span className={`feedback-linked-detail-value ${truncate ? 'feedback-linked-detail-truncate' : ''}`}>{value}</span>
        </div>
    );
}
